import Link from 'next/link';
import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
    useCheckoutByEmail,
    useCheckoutByPhone,
    useVerificationCodeByEmail,
    useVerificationCodeByPhone,
} from '@api/auth';

import { isEmailValid } from '@utils/emailChecker';
import { phoneFormat } from '@utils/phoneFormat';

import FormMessage from '@components/controls/Form/Message';
import Button from '@components/uiKit/Button';
import { LoadingSpinner } from '@components/uiKit/LoadingSpinner';

import PhoneEmailInput from './PhoneEmailInput';
import styles from './SignIn.module.scss';
import { SignInFormProps } from './types';
import { determineFieldType } from './utils';

interface SignInFormValues {
    phone: string;
}

export const SignIn: FC<SignInFormProps> = ({
    setEmail,
    errorMessage,
    setPhone,
    onChangeToRegistration,
    onChangeEmailToLogin,
    onChangePhoneToLogin,
    setIsLogin,
    setErrorMessage,
    path,
    onStartTimer,
    hidePrimaryActions = false,
    bindSubmit,
}) => {
    const { mutateAsync: checkoutByPhone, error: errorCheckoutPhone } = useCheckoutByPhone();
    const { mutateAsync: checkoutByEmail, isPending: isLoadingCheckoutEmail } = useCheckoutByEmail();

    const { mutateAsync: verificationCodeByPhone, isPending: isLoadingVerificationPhone } =
        useVerificationCodeByPhone();

    const { mutateAsync: verificationCodeByEmail, isPending: isLoadingVerificationByEmail } =
        useVerificationCodeByEmail();

    const isLoadingCheckout = isLoadingCheckoutEmail;

    const isLoadingVerification = isLoadingVerificationPhone || isLoadingVerificationByEmail;

    const { control, handleSubmit } = useForm<SignInFormValues>({
        defaultValues: { phone: '' },
    });

    const onSubmit = async ({ phone }: SignInFormValues) => {
        setErrorMessage('');

        if (!phone || phone.trim() === '') {
            setErrorMessage('Введите номер телефона или email');

            return;
        }

        const fieldType = determineFieldType(phone);

        if (fieldType === 'email') {
            if (!isEmailValid(phone)) {
                setErrorMessage('Некорректный email');

                return;
            }

            try {
                const result = await checkoutByEmail({ email: phone });
                if (result.data && result.data.found) {
                    setEmail(phone);
                    await verificationCodeByEmail({ email: phone });
                    onStartTimer?.();
                    setIsLogin(true);
                    onChangeEmailToLogin();
                } else {
                    setEmail(phone);
                    setIsLogin(false);
                    onChangeToRegistration();
                }
            } catch (error: any) {
                setErrorMessage(error.message);
            }
        } else {
            try {
                const changedPhone = phoneFormat(phone);
                const result = await checkoutByPhone({ phone: changedPhone });

                if (result.data && result.data.found) {
                    setPhone(changedPhone);
                    await verificationCodeByPhone({
                        phone: changedPhone,
                    });
                    onStartTimer?.();
                    setIsLogin(true);
                    onChangePhoneToLogin();
                } else {
                    setPhone(changedPhone);
                    onChangeToRegistration();
                    setIsLogin(false);
                }
            } catch (error: any) {
                setErrorMessage(error.message);
            }
        }
    };

    useEffect(() => {
        return () => setErrorMessage('');
    }, []);

    useEffect(() => {
        if (!bindSubmit) {
            return;
        }

        bindSubmit(() => handleSubmit(onSubmit)());

        return () => bindSubmit(null);
    }, [bindSubmit, handleSubmit]);

    return (
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.fields}>
                <div className={styles.fieldItem}>
                    <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                            <PhoneEmailInput
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                error={!!errorMessage}
                                isLoading={isLoadingCheckout}
                                autoFocus
                                onClearError={() => setErrorMessage('')}
                            />
                        )}
                    />
                </div>
            </div>
            {errorMessage && (
                <FormMessage
                    type="error"
                    message={errorMessage || errorCheckoutPhone?.message || ''}
                    className={styles.error}
                />
            )}

            {!hidePrimaryActions && (
                <Button
                    type="submit"
                    variant="mainOrange"
                    size="lg"
                    fullWidth
                    data-testid="button-submit"
                    role="button"
                    className={styles.submit}
                >
                    <span className={styles.submitLabel}>
                        Продолжить
                        {isLoadingVerification && (
                            <span className={styles.spinner}>
                                <LoadingSpinner />
                            </span>
                        )}
                    </span>
                </Button>
            )}
            {path && (
                <div className={styles.backLink}>
                    <Link legacyBehavior href="/" passHref prefetch={false}>
                        <a data-testid="button-go-back">Вернуться к товарам</a>
                    </Link>
                </div>
            )}
        </form>
    );
};
